import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, RefreshCw, MessageSquareText, ShieldCheck, Search, X } from "lucide-react";
import { OperationalShell } from "../components/OperationalShell";
import { useAuth } from "../../lib/auth";
import { useCapability } from "../useCapability";
import {
    SeverityChip,
    RecoveryConfidenceChip,
    InsightStrip,
    NextActionRow,
    TimingFootprint,
    GroupHeader,
} from "../../components/intelligence";
import HighlightedText from "../../components/shared/HighlightedText";
import api from "../../lib/api";

/**
 * Phase 8.1 — Review Shield Intelligence (native operational triage).
 *
 * Consumes `/api/operational-base/review-shield/triage` and renders the
 * 4-group rules-based triage as a calm operator surface. This is NOT
 * a dashboard — it is operational prioritization infrastructure.
 *
 * Demo Mode handled entirely in the backend (synthetic Blue Flame lens).
 */

/* ─────────── Local registry — stable group ordering + metadata ─────────── */
const TRIAGE_GROUP_REGISTRY = {
    immediate_attention: {
        order: 1,
        labelKey: "intel.group.immediate_attention",
        icon: "alert-octagon",
        tone: "supportive",
        accent: "#FB923C",
    },
    recovery_in_progress: {
        order: 2,
        labelKey: "intel.group.recovery_in_progress",
        icon: "life-buoy",
        tone: "calm",
        accent: "#A7F3D0",
    },
    monitoring: {
        order: 3,
        labelKey: "intel.group.monitoring",
        icon: "activity",
        tone: "calm",
        accent: "#94A3B8",
    },
    resolved: {
        order: 4,
        labelKey: "intel.group.resolved",
        icon: "check-circle-2",
        tone: "calm",
        accent: "#A7F3D0",
    },
};

/* ─────────── Small star readout ─────────── */
const StarReadout = ({ count = 0 }) => (
    <span
        className="inline-flex items-center gap-0.5"
        data-testid="intel-stars"
        data-insight-id="review-shield.star-rating"
        data-insight-label="Star rating"
        aria-label={`${count} of 5 stars`}
    >
        {[1, 2, 3, 4, 5].map((s) => (
            <Star
                key={s}
                size={12}
                strokeWidth={1.5}
                fill={s <= count ? "#FBBF24" : "transparent"}
                stroke={s <= count ? "#FBBF24" : "rgba(148, 163, 184, 0.55)"}
            />
        ))}
    </span>
);

/* ─────────── Single triage item card ─────────── */
const TriageItemCard = ({ item, onDraftReply, searchQuery }) => {
    const { t } = useTranslation();
    const hasId = !!item?.id;
    return (
        <article
            className="ob-card p-5"
            data-testid={`intel-item-${item.id}`}
            data-severity={item.severity}
        >
            {/* Top row — rating + location + timing + chips */}
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <StarReadout count={Number(item.rating) || 0} />
                    {item.location_name && (
                        <span
                            className="text-xs truncate"
                            style={{
                                color: "var(--ob-text-secondary)",
                                fontFamily: "Outfit, sans-serif",
                                maxWidth: "22ch",
                            }}
                            data-testid={`intel-item-location-${item.id}`}
                        >
                            <HighlightedText text={item.location_name} query={searchQuery} />
                        </span>
                    )}
                    <span data-insight-id="review-shield.recovery-timer">
                        <TimingFootprint
                            ageHours={Number(item.age_hours) || 0}
                            hasResponse={!!item.has_response}
                            testId={`intel-timing-${item.id}`}
                        />
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-insight-id="review-shield.severity-chip">
                    <SeverityChip
                        severity={item.severity}
                        testId={`intel-severity-${item.id}`}
                    />
                    {item.recovery_confidence && (
                        <RecoveryConfidenceChip
                            confidence={item.recovery_confidence}
                            testId={`intel-confidence-${item.id}`}
                        />
                    )}
                </div>
            </header>

            {/* v4.34.5 — Message preview with operator-search highlighting.
                Empty messages render an italic placeholder so an empty card
                is never confused with a render bug. */}
            <p
                className="mt-4 text-sm leading-relaxed"
                style={{
                    color: item.message ? "var(--ob-text-primary)" : "var(--ob-text-tertiary)",
                    fontStyle: item.message ? "normal" : "italic",
                }}
                data-testid={`intel-message-${item.id}`}
            >
                {item.message
                    ? <HighlightedText text={item.message} query={searchQuery} />
                    : t("intel.page.no_message")}
            </p>

            {/* Insight strip */}
            <InsightStrip
                reasons={item.severity_reasons || []}
                testId={`intel-insight-${item.id}`}
            />

            {/* Next action row */}
            <NextActionRow
                actions={item.next_actions || []}
                testId={`intel-actions-${item.id}`}
            />

            {/* Bottom CTA — draft reply */}
            <div className="mt-4 flex justify-end">
                <button
                    type="button"
                    onClick={() => hasId && onDraftReply(item.id)}
                    disabled={!hasId}
                    data-testid={`intel-draft-reply-${item.id}`}
                    data-insight-id="review-shield.respond-with-assistant"
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    style={{
                        color: "var(--ob-text-secondary)",
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        fontFamily: "Outfit, sans-serif",
                    }}
                >
                    <MessageSquareText size={12} strokeWidth={1.75} />
                    {t("intel.page.respond_with_assistant")}
                </button>
            </div>
        </article>
    );
};

/* ─────────── Page ─────────── */
const ReviewShieldIntelligence = () => {
    const { t, i18n } = useTranslation();
    const nav = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const { map } = useCapability();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [fetchedAt, setFetchedAt] = useState(null);
    const [ageLabel, setAgeLabel] = useState("");
    // v4.34.5 — Operator keyword search. `searchInput` is the controlled
    // typing buffer; `effectiveSearch` is the 250ms-debounced trigger that
    // actually re-fetches. Decouples typing speed from request volume.
    const [searchInput, setSearchInput] = useState("");
    const [effectiveSearch, setEffectiveSearch] = useState("");
    const scrollRef = useRef(0);

    useEffect(() => {
        const handle = setTimeout(() => setEffectiveSearch(searchInput.trim()), 250);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const load = async (silent = false) => {
        if (silent) {
            scrollRef.current = window.scrollY;
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(false);
        try {
            const lang = (i18n.language || "en").split("-")[0];
            // v4.34.5 — `q` keyword is server-evaluated against message,
            // location_name, category and organization_name (case-insensitive,
            // regex-safe). Limit auto-rises to 1000 when q is present —
            // see backend router_triage.py.
            const params = new URLSearchParams({ lang });
            if (effectiveSearch) params.set("q", effectiveSearch);
            const res = await api.get(`/operational-base/review-shield/triage?${params.toString()}`);
            setPayload(res.data || null);
            setFetchedAt(new Date());
        } catch (err) {
            setError(true);
            if (!silent) {
                toast.error(err?.response?.data?.detail || t("intel.page.error"));
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
            if (silent && scrollRef.current) {
                // Preserve scroll position after refresh
                requestAnimationFrame(() => window.scrollTo(0, scrollRef.current));
            }
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, effectiveSearch]);

    // Calm "updated X minutes ago" recompute every 30s.
    useEffect(() => {
        if (!fetchedAt) {
            setAgeLabel("");
            return undefined;
        }
        const compute = () => {
            const mins = Math.floor((Date.now() - fetchedAt.getTime()) / 60000);
            if (mins < 1) setAgeLabel(t("intel.page.updated_just_now", "Updated just now"));
            else if (mins === 1) setAgeLabel(t("intel.page.updated_one_minute_ago", "Updated 1 minute ago"));
            else setAgeLabel(t("intel.page.updated_minutes_ago", "Updated {{count}} minutes ago", { count: mins }));
        };
        compute();
        const id = setInterval(compute, 30000);
        return () => clearInterval(id);
    }, [fetchedAt, t]);

    /* ─────────── Group ordering ─────────── */
    const orderedGroups = useMemo(() => {
        const raw = Array.isArray(payload?.groups) ? payload.groups : [];
        return [...raw].sort((a, b) => {
            const ao = TRIAGE_GROUP_REGISTRY[a.key]?.order ?? 99;
            const bo = TRIAGE_GROUP_REGISTRY[b.key]?.order ?? 99;
            return ao - bo;
        });
    }, [payload]);

    const totalCount = payload?.totals?.all ?? 0;

    const handleDraftReply = (feedbackId) => {
        if (!feedbackId) return;
        nav(`/operational-base/review-assistant?feedback_id=${encodeURIComponent(feedbackId)}`);
    };

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!map) return null;

    return (
        <OperationalShell breadcrumb={[{ label: t("intel.page.eyebrow") }]}>
            {/* ─── Header ─── */}
            <header
                className="mb-10 lg:mb-14 flex items-start justify-between gap-6 flex-wrap"
                data-testid="intel-page-header"
            >
                <div className="space-y-3 min-w-0">
                    <p className="ob-eyebrow">{t("intel.page.eyebrow")}</p>
                    <h1
                        className="text-4xl sm:text-5xl flex items-center gap-3"
                        style={{ color: "var(--ob-text-primary)" }}
                        data-testid="intel-page-title"
                        data-insight-id="review-shield.overview"
                        data-insight-primary="true"
                    >
                        <ShieldCheck size={28} strokeWidth={1.5} style={{ color: "#A7F3D0" }} />
                        {t("intel.page.title")}
                    </h1>
                    <p
                        className="text-base max-w-2xl"
                        style={{ color: "var(--ob-text-secondary)" }}
                        data-testid="intel-page-subtitle"
                    >
                        {t("intel.page.subtitle")}
                    </p>
                    {ageLabel && (
                        <p
                            className="text-[11px] uppercase tracking-wider"
                            style={{ color: "var(--ob-text-tertiary)" }}
                            data-testid="intel-page-updated"
                        >
                            {ageLabel}
                        </p>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    {totalCount > 0 && (
                        <span
                            data-testid="intel-page-total"
                            data-insight-id="review-shield.total-count"
                            data-insight-label="Total open feedback"
                            className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5"
                            style={{
                                color: "var(--ob-text-secondary)",
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontFamily: "Outfit, sans-serif",
                            }}
                        >
                            <span style={{ color: "var(--ob-text-tertiary)" }}>
                                {t("intel.page.totals_label")}
                            </span>
                            <span style={{ color: "var(--ob-text-primary)", fontWeight: 500 }}>
                                {totalCount}
                            </span>
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        data-testid="intel-page-refresh"
                        data-insight-id="review-shield.refresh"
                        data-insight-label="Refresh feedback"
                        className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 disabled:opacity-50"
                        style={{
                            color: "var(--ob-text-secondary)",
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <RefreshCw
                            size={12}
                            strokeWidth={1.5}
                            className={refreshing ? "animate-spin" : ""}
                        />
                        {t("intel.page.refresh", "Refresh")}
                    </button>
                </div>
            </header>

            {/* v4.34.5 — Operator keyword search.
                Calm pill aesthetic matching the rest of Operational Base.
                Debounced 250ms; null search restores the full triage payload. */}
            <div
                className="mb-6 flex items-center gap-3 flex-wrap"
                data-testid="intel-page-search-row"
            >
                <div
                    className="relative flex-1 min-w-[240px] max-w-xl"
                    data-insight-id="review-shield.operator-search"
                >
                    <Search
                        size={14}
                        strokeWidth={1.75}
                        className="absolute left-3 top-1/2 -translate-y-1/2"
                        style={{ color: "var(--ob-text-tertiary)" }}
                    />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t(
                            "intel.page.search_placeholder",
                            "Search messages, locations, categories…"
                        )}
                        className="w-full text-sm rounded-full pl-9 pr-9 py-2 outline-none transition-colors"
                        style={{
                            color: "var(--ob-text-primary)",
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.10)",
                            fontFamily: "Outfit, sans-serif",
                        }}
                        data-testid="intel-page-search-input"
                        aria-label={t("intel.page.search_aria", "Search feedback")}
                    />
                    {searchInput && (
                        <button
                            type="button"
                            onClick={() => setSearchInput("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10"
                            style={{ color: "var(--ob-text-tertiary)" }}
                            data-testid="intel-page-search-clear"
                            aria-label={t("common.clear", "Clear")}
                        >
                            <X size={12} strokeWidth={2} />
                        </button>
                    )}
                </div>
                {effectiveSearch && (
                    <span
                        className="text-xs"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="intel-page-search-count"
                    >
                        {totalCount === 0
                            ? t(
                                "intel.page.search_no_results",
                                'No feedback matches "{{q}}"',
                                { q: effectiveSearch }
                              )
                            : t(
                                "intel.page.search_results_count",
                                '{{count}} result(s) for "{{q}}"',
                                { count: totalCount, q: effectiveSearch }
                              )}
                    </span>
                )}
            </div>

            {/* ─── Loading ─── */}
            {loading && (
                <p
                    data-testid="intel-page-loading"
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    {t("intel.page.loading")}
                </p>
            )}

            {/* ─── Error ─── */}
            {!loading && error && !payload && (
                <div
                    className="ob-card p-8 text-center"
                    data-testid="intel-page-error"
                >
                    <p
                        className="text-base"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        {t("intel.page.error")}
                    </p>
                </div>
            )}

            {/* ─── Empty ─── */}
            {!loading && payload && totalCount === 0 && (
                <div
                    className="ob-card p-10 text-center"
                    data-testid="intel-page-empty"
                >
                    <h2
                        className="text-lg mb-2"
                        style={{
                            color: "var(--ob-text-primary)",
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 500,
                        }}
                    >
                        {t("intel.page.empty_title")}
                    </h2>
                    <p
                        className="text-sm max-w-md mx-auto"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        {t("intel.page.empty_subtitle")}
                    </p>
                </div>
            )}

            {/* ─── Triage groups ─── */}
            {!loading && payload && totalCount > 0 && (
                <div className="space-y-2" data-testid="intel-page-groups" data-insight-id="review-shield.triage-groups">
                    {orderedGroups.map((group) => {
                        const meta = TRIAGE_GROUP_REGISTRY[group.key] || {};
                        if (!group.count || !group.items?.length) return null;
                        return (
                            <section
                                key={group.key}
                                data-testid={`intel-group-section-${group.key}`}
                            >
                                <GroupHeader
                                    groupKey={group.key}
                                    labelKey={group.label_key || meta.labelKey}
                                    icon={group.icon || meta.icon}
                                    count={group.count}
                                    tone={group.tone || meta.tone}
                                />
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {group.items.map((item) => (
                                        <TriageItemCard
                                            key={item.id}
                                            item={item}
                                            onDraftReply={handleDraftReply}
                                            searchQuery={effectiveSearch}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </OperationalShell>
    );
};

export default ReviewShieldIntelligence;
